import { useState } from "react";
import {
  OrganizationSize,
  ProbabilityLevel,
  AttackCategoryData,
  ImpactLevel,
  SectorAnalysisDiff,
} from "@/types/risk-analysis";
import { ACTORI_AMENINTARE } from "@/data/risk-sectors";
import { RiskScoreCell } from "./RiskScoreCell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn, formatScore } from "@/lib/utils";
import { ColoredDropdown } from "../common/ColoredDropdown";
import {
  IMPACT_OPTIONS,
  PROBABILITY_OPTIONS,
} from "../../constants/risk-options";

interface RiskMatrixCarouselProps {
  attackMatrix: AttackCategoryData[];
  actorTotals: Record<string, number>;
  totalScore: number;
  orgSize: OrganizationSize;
  onProbabilityChange: (
    categoryId: string,
    actorId: string,
    probability: ProbabilityLevel
  ) => void;
  onImpactChange?: (categoryId: string, impact: ImpactLevel) => void;
  disabled?: boolean;
  diff?: SectorAnalysisDiff | null;
}

export function RiskMatrixCarousel({
  attackMatrix,
  actorTotals,
  totalScore,
  orgSize,
  onProbabilityChange,
  onImpactChange,
  disabled = false,
  diff,
}: RiskMatrixCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentCategory = attackMatrix[currentIndex];

  // Find changes for the current category
  const categoryChange = diff?.categoryChanges.find(
    (c) => c.categoryId === currentCategory?.categoryId
  );
  const hasImpactChange = !!categoryChange?.impactChange;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : attackMatrix.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < attackMatrix.length - 1 ? prev + 1 : 0));
  };

  if (!currentCategory) return null;

  const orgSizeValue = orgSize === "S" ? "1" : orgSize === "M" ? "2" : "3";

  return (
    <div className="space-y-4">
      {/* Navigation buttons above the card */}
      <div className="flex justify-end items-center gap-2">
        <Button variant="outline" size="icon" onClick={handlePrev}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="md:text-sm text-xs text-muted-foreground">
          {currentIndex + 1} / {attackMatrix.length}
        </span>
        <Button variant="outline" size="icon" onClick={handleNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Header with table */}
      <Card>
        <CardHeader className="p-2">
          <table className="w-full border-collapse border border-black border-2">
            <thead>
              {/* Row 1 */}
              <tr>
                <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-left">
                  Dimensiunea entității
                </th>
                <th className="bg-blue-300 font-bold text-center text-sm border border-black px-2 py-1">
                  {orgSizeValue}
                </th>
                <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                  Categorii de actori
                </th>
              </tr>

              {/* Row 2 */}
              <tr>
                <th className="bg-blue-700 text-white font-bold text-sm border border-black px-2 py-1 text-left">
                  Categoria atacului cibernetic
                </th>
                <th className="bg-blue-700 text-white italic font-bold text-sm border border-black px-2 py-1 text-center">
                  Natura atacului
                </th>
                <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                  Impact
                </th>
              </tr>

              {/* Row 3 */}
              <tr>
                <th className="bg-blue-300 font-bold text-sm border border-black px-2 py-1 text-left">
                  {currentCategory.categoryName}
                </th>
                <th className="text-center font-bold text-sm border border-black px-2 py-1">
                  {currentCategory.tipAtac === "global" ? "1" : "2"}
                </th>
                <th className="border border-black p-0">
                  <ColoredDropdown
                    options={IMPACT_OPTIONS}
                    value={currentCategory.impact}
                    onChange={(impact) =>
                      onImpactChange?.(currentCategory.categoryId, impact)
                    }
                    disabled={disabled || !onImpactChange}
                    hasChanged={hasImpactChange}
                  />
                </th>
              </tr>
            </thead>
          </table>
        </CardHeader>
      </Card>

      {/* Actor Cards */}
      <div className="space-y-3">
        {ACTORI_AMENINTARE.map((actor) => {
          const actorData = currentCategory.actors[actor.id];

          // Check if this actor's probability changed
          const hasProbabilityChange =
            !!categoryChange?.probabilityChanges.find(
              (pc) => pc.actorId === actor.id
            );

          // Score changes when either probability OR impact changes
          const hasScoreChange = hasImpactChange || hasProbabilityChange;

          return (
            <Card className="p-2" key={actor.id}>
              <table className="w-full border-collapse border border-black border-2">
                <thead>
                  {/* Row 1: Competence Level */}
                  <tr>
                    <th
                      colSpan={2}
                      className="border border-black [border-bottom-style:dashed] px-2 py-1 text-center text-xs font-semibold italic bg-white"
                    >
                      {actor.categorie === "Common skills"
                        ? "Competențe comune"
                        : "Competențe extinse"}
                    </th>
                  </tr>

                  {/* Row 2: Actor Name */}
                  <tr>
                    <th
                      colSpan={2}
                      className="border border-black [border-top-style:dashed] px-2 py-1 text-center text-xs font-bold bg-white"
                    >
                      {actor.nume}
                    </th>
                  </tr>

                  {/* Row 3: Column Headers */}
                  <tr>
                    <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                      Probabilitate
                    </th>
                    <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                      Valoarea riscului
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 4: Data Cells */}
                  <tr>
                    <td className="border border-black p-0">
                      <ColoredDropdown
                        options={PROBABILITY_OPTIONS}
                        value={actorData.probability}
                        onChange={(prob) =>
                          onProbabilityChange(
                            currentCategory.categoryId,
                            actor.id,
                            prob
                          )
                        }
                        disabled={disabled}
                        hasChanged={hasProbabilityChange}
                      />
                    </td>
                    <td
                      className={cn(
                        "border border-black px-2 py-1 text-center font-medium",
                        hasScoreChange && "font-bold"
                      )}
                    >
                      {formatScore(actorData.score)}
                      {hasScoreChange && "*"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          );
        })}
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center gap-2 pt-4">
        {attackMatrix.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={cn(
              "w-2 h-2 rounded-full transition-all",
              idx === currentIndex ? "bg-primary w-6" : "bg-muted-foreground/30"
            )}
            aria-label={`Go to category ${idx + 1}`}
          />
        ))}
      </div>

      {/* Actor Totals Summary */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="md:text-base text-sm">
            Total per Actor (toate categoriile)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {ACTORI_AMENINTARE.map((actor) => (
              <div
                key={actor.id}
                className="flex flex-col md:items-center gap-1"
              >
                <span className="text-xs text-muted-foreground text-center">
                  {actor.nume}
                </span>
                <RiskScoreCell score={actorTotals[actor.id] || 0} size="sm" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
